/**
 * Estado de la puerta de acceso (login.html: acceso / sin-acceso / caducada /
 * red-caída), derivado de lo que MSAL cuenta — no de un conmutador de dev.
 * También acepta ?estado= para revisar los banners a mano.
 */
import { EventType, InteractionRequiredAuthError, type EventMessage } from "@azure/msal-browser";
import { useSyncExternalStore } from "react";

export type LoginError = "sin-acceso" | "caducada" | "red";
const KEY = "studio-login-error";
const listeners = new Set<() => void>();

export function setLoginError(e: LoginError | null): void {
  try { e ? sessionStorage.setItem(KEY, e) : sessionStorage.removeItem(KEY); } catch { /* sin storage */ }
  if (!e && new URLSearchParams(window.location.search).has("estado")) {
    const url = new URL(window.location.href); url.searchParams.delete("estado");
    history.replaceState(null, "", url);
  }
  for (const l of listeners) l();
}
export function readLoginError(): LoginError | null {
  const q = new URLSearchParams(window.location.search).get("estado");
  if (q === "sin-acceso" || q === "caducada" || q === "red") return q;
  try { return (sessionStorage.getItem(KEY) as LoginError | null) ?? null; } catch { return null; }
}
export function useLoginError(): LoginError | null {
  return useSyncExternalStore((cb) => { listeners.add(cb); return () => listeners.delete(cb); }, readLoginError, () => null);
}
/**
 * Clasifica un fallo de MSAL. «sin-acceso» es SOLO cuenta no asignada
 * (AADSTS50105) o consentimiento denegado por admin (AADSTS65001); cancelar
 * el SSO (access_denied/user_cancelled) o una interacción pendiente no es un
 * error de la puerta → null (estado acceso, botón activo).
 */
export function classify(err: unknown): LoginError | null {
  const text = `${String((err as { errorCode?: string })?.errorCode ?? "")} ${String((err as Error)?.message ?? "")}`;
  if (/AADSTS50105|AADSTS65001/i.test(text)) return "sin-acceso";
  if (/access_denied|user_cancelled|consent_required|interaction_required/i.test(text)) return null;
  if (err instanceof InteractionRequiredAuthError) return "caducada";
  return "red";
}
export function onMsalEvent(e: EventMessage): void {
  if (e.eventType === EventType.LOGIN_SUCCESS || e.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) setLoginError(null);
  if (e.eventType === EventType.LOGIN_FAILURE) setLoginError(classify(e.error));
}
/** En «red», sondea Entra cada 30 s y retira el banner cuando responde (promesa del prototipo). */
export function watchEntra(authority: string, every = 30_000): () => void {
  const id = window.setInterval(() => {
    if (readLoginError() !== "red") return;
    fetch(`${authority}/v2.0/.well-known/openid-configuration`, { mode: "cors" }).then((r) => { if (r.ok) setLoginError(null); }).catch(() => {});
  }, every);
  return () => window.clearInterval(id);
}
